import { get } from 'lodash-es';
import { getPredictionOutcomeEventID } from '../../../../providers/SecuritiesProvider/predictionIndexes';
import type { Security } from '../../../../types/Security';
import { SecurityTypesUtils } from '../../../../types/SecurityTypes';
import type { IPredictionOutcomeEvent, IPredictionOutcomeEventSeriesRecord } from '../../../../types/types';

export const PREDICTION_CATEGORY_OTHER = 'Other';

export type PredictionMarketColumnParams = {
  /** Row field holding the security symbol. Defaults to `Symbol`. Positions use `Asset`. */
  symbolField?: string;
};

export type PredictionMarketResolvedFields = {
  category: string;
  seriesID: string | undefined;
  seriesName: string;
  eventID: string | undefined;
  eventName: string;
};

export type PredictionMarketLookup = {
  securitiesBySymbol?: Map<string, Security>;
  predictionEventsByID?: Map<string, IPredictionOutcomeEvent>;
  predictionEventSeriesByID?: Map<string, IPredictionOutcomeEventSeriesRecord>;
};

const EMPTY_PREDICTION_EVENTS_BY_ID = new Map<string, IPredictionOutcomeEvent>();
const EMPTY_PREDICTION_EVENT_SERIES_BY_ID = new Map<string, IPredictionOutcomeEventSeriesRecord>();

export function getPredictionEventForSecurity(
  security: Security,
  eventsByID: Map<string, IPredictionOutcomeEvent>
): IPredictionOutcomeEvent | undefined {
  const eventID = getPredictionOutcomeEventID(security);
  if (eventID == null) {
    return undefined;
  }
  return eventsByID.get(eventID);
}

export function getPredictionCategoryTab(category: string | undefined): string {
  const trimmed = category?.trim();
  return trimmed ? trimmed : PREDICTION_CATEGORY_OTHER;
}

export function resolvePredictionEventSeries(
  event: IPredictionOutcomeEvent | undefined,
  eventSeriesByID: Map<string, IPredictionOutcomeEventSeriesRecord>
): IPredictionOutcomeEventSeriesRecord | undefined {
  if (event?.SeriesID == null) {
    return undefined;
  }
  return eventSeriesByID.get(event.SeriesID);
}

export function getPredictionSecurityFromRow(
  data: unknown,
  context: PredictionMarketLookup | undefined,
  params: PredictionMarketColumnParams | undefined
): Security | undefined {
  const symbolField = params?.symbolField ?? 'Symbol';
  const value = data != null ? get(data, symbolField) : undefined;
  if (typeof value !== 'string') {
    return undefined;
  }
  const security = context?.securitiesBySymbol?.get(value);
  if (!security) {
    return undefined;
  }
  if (!SecurityTypesUtils.PredictionOutcome.isProductType(security)) {
    return undefined;
  }
  return security;
}

function emptyResolvedFields(): PredictionMarketResolvedFields {
  return {
    category: '',
    seriesID: undefined,
    seriesName: '',
    eventID: undefined,
    eventName: '',
  };
}

/**
 * Resolves category / series / event display fields for a prediction-outcome symbol.
 */
export function resolvePredictionMarketFields(
  symbol: string | undefined,
  lookup: PredictionMarketLookup
): PredictionMarketResolvedFields {
  const empty = emptyResolvedFields();

  if (symbol == null) {
    return empty;
  }

  const security = lookup.securitiesBySymbol?.get(symbol);
  if (!security) {
    return empty;
  }

  if (!SecurityTypesUtils.PredictionOutcome.isProductType(security)) {
    return empty;
  }

  const eventsByID = lookup.predictionEventsByID ?? EMPTY_PREDICTION_EVENTS_BY_ID;
  const eventSeriesByID = lookup.predictionEventSeriesByID ?? EMPTY_PREDICTION_EVENT_SERIES_BY_ID;
  const event = getPredictionEventForSecurity(security, eventsByID);
  const series = resolvePredictionEventSeries(event, eventSeriesByID);

  const categorySource = series?.Category ?? event?.Category;
  const seriesName = series?.SeriesName;
  const eventName = event?.EventName;

  return {
    category: categorySource?.trim() || '',
    seriesID: series?.SeriesID,
    seriesName: seriesName ?? '',
    eventID: event?.EventID,
    eventName: eventName ?? '',
  };
}

export function resolvePredictionMarketFieldsFromRow(
  data: unknown,
  context: PredictionMarketLookup | undefined,
  params: PredictionMarketColumnParams | undefined
): PredictionMarketResolvedFields {
  const symbolField = params?.symbolField ?? 'Symbol';
  const value = data != null ? get(data, symbolField) : undefined;
  const symbol = typeof value === 'string' ? value : undefined;
  return resolvePredictionMarketFields(symbol, {
    securitiesBySymbol: context?.securitiesBySymbol,
    predictionEventsByID: context?.predictionEventsByID,
    predictionEventSeriesByID: context?.predictionEventSeriesByID,
  });
}
